import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Field, Select, Input, Empty } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Upload, Calculator, Lock, CheckCircle2, FileSpreadsheet, ReceiptText, Eye, RotateCcw, Pencil, Save, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inr = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COLS = [
  ["snapshot.uan", "UAN"], ["snapshot.name", "Employee"], ["snapshot.designation", "Designation"],
  ["working_days", "Days"], ["wage_rate", "Rate"], ["earned_wage", "Earned"],
  ["employee_pf", "Emp PF"], ["employee_esi", "Emp ESI"], ["pt", "PT"],
  ["total_deductions", "Total Ded"], ["net_pay", "Net Pay"],
  ["employer_pf", "Er PF"], ["employer_esi", "Er ESI"], ["service_charge", "Svc Chg"], ["client_cost", "Client Cost"],
];
const get = (e, p) => (p.includes(".") ? e[p.split(".")[0]]?.[p.split(".")[1]] : e[p]);

export default function Payroll() {
  const { isSuper } = useAuth();
  const [searchParams] = useSearchParams();

  const queryMonth =
    searchParams.get("month") || "";

  const queryStatus =
    searchParams.get("status") || "";

  const queryClient =
    searchParams.get("client_id") || "";

  const queryRunId =
    searchParams.get("run_id") || "";

  const queryView =
    searchParams.get("view") || "";

  const [runs, setRuns] = useState([]);
  const [clients, setClients] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [editRun, setEditRun] = useState(null);
  const [active, setActive] = useState(null); // run detail

  const load = () =>
    api.get("/payroll/runs").then((r) =>
      setRuns(r.data)
    );

  useEffect(() => {
    load();
    api.get("/clients").then((r) =>
      setClients(r.data)
    );
  }, []);

  useEffect(() => {
    if (queryRunId && runs.some((r) => r.id === queryRunId)) {
      setActive(queryRunId);
    }
  }, [queryRunId, runs]);

  const cname = (id) =>
    clients.find((c) => c.id === id)?.name || "—";

  const filteredRuns = runs.filter((r) => {
    if (
      queryMonth &&
      r.month !== queryMonth
    ) {
      return false;
    }

    if (
      queryClient &&
      queryClient !== "all" &&
      r.client_id !== queryClient
    ) {
      return false;
    }

    if (queryStatus === "processed") {
      return [
        "calculated",
        "finalized",
        "locked",
      ].includes(r.status);
    }

    if (queryStatus === "pending") {
      return [
        "draft",
        "imported",
      ].includes(r.status);
    }

    if (
      queryStatus &&
      !["processed", "pending"].includes(queryStatus)
    ) {
      return r.status === queryStatus;
    }

    return true;
  });

  const deleteRun = async (run) => {
    const clientName = cname(run.client_id);

    const ok = window.confirm(
      `Delete payroll run for ${clientName} - ${run.month}?\n\n` +
      `Status: ${run.status}\n` +
      `Employees: ${run.employee_count || 0}\n\n` +
      `This will permanently remove this payroll run and its imported/calculated data.`
    );

    if (!ok) return;

    try {
      await api.delete(`/payroll/runs/${run.id}`);
      toast.success("Payroll run deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (active) return <RunDetail runId={active} onBack={() => { setActive(null); load(); }} clients={clients} />;

  return (
    <div>
      <PageHeader
        title="Payroll"
        subtitle={
          queryView === "net_salary"
            ? `Net salary payroll runs${queryMonth ? ` · ${queryMonth}` : ""}`
            : `Monthly payroll runs — upload, calculate, finalize${queryMonth ? ` · ${queryMonth}` : ""}`
        }
        actions={
          <Btn
            onClick={() => setNewOpen(true)}
            data-testid="new-run-btn"
          >
            <Plus size={16} />
            New Payroll Run
          </Btn>
        }
      />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {filteredRuns.length ? (
            <table className="nk-table" data-testid="runs-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Month</th>
                  <th>Employees</th>
                  <th>Net Pay</th>
                  <th>Client Cost</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((r) => (
                  <tr key={r.id} data-testid={`run-row-${r.id}`}>
                    <td className="font-medium">{cname(r.client_id)}</td><td className="nk-num">{r.month}</td>
                    <td className="nk-num">{r.employee_count}</td>
                    <td className="nk-num">₹{inr(r.totals?.net_pay)}</td>
                    <td className="nk-num">₹{inr(r.totals?.client_cost)}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Btn
                          variant="outline"
                          onClick={() => setActive(r.id)}
                          data-testid={`open-run-${r.id}`}
                        >
                          <Eye size={14} /> Open
                        </Btn>

                        {isSuper && r.status === "draft" && (
                          <Btn
                            variant="outline"
                            onClick={() => setEditRun(r)}
                            data-testid={`edit-run-${r.id}`}
                          >
                            <Pencil size={14} /> Edit
                          </Btn>
                        )}

                        {isSuper &&
                          !["finalized", "locked"].includes(r.status) && (
                            <Btn
                              variant="outline"
                              onClick={() => deleteRun(r)}
                              data-testid={`delete-run-${r.id}`}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} /> Delete
                            </Btn>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty title="No payroll runs" hint="Create a run to begin the monthly workflow." />}
        </div>
      </Card>
      {newOpen && (
        <NewRunDialog
          clients={clients}
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            setNewOpen(false);
            load();
            setActive(id);
          }}
        />
      )}

      {editRun && (
        <EditRunDialog
          run={editRun}
          clients={clients}
          onClose={() => setEditRun(null)}
          onSaved={() => {
            setEditRun(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function NewRunDialog({ clients, onClose, onCreated }) {
  const [clientId, setClientId] = useState("");
  const [contractId, setContractId] = useState("");
  const [contracts, setContracts] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => {
    if (clientId) api.get(`/clients/${clientId}/contracts`).then((r) => setContracts(r.data));
    else setContracts([]);
  }, [clientId]);
  const create = async () => {
    if (!clientId || !month) { toast.error("Select client and month"); return; }
    try {
      const { data } = await api.post("/payroll/runs", { client_id: clientId, contract_id: contractId || null, month });
      onCreated(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Payroll Run</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Client">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="run-client">
              <option value="">Select client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Contract">
            <Select value={contractId} onChange={(e) => setContractId(e.target.value)} data-testid="run-contract">
              <option value="">All / None</option>{contracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Payroll Month"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="run-month" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn onClick={create} data-testid="create-run-btn">Create Run</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditRunDialog({
  run,
  clients,
  onClose,
  onSaved,
}) {
  const [clientId, setClientId] = useState(
    run.client_id || ""
  );

  const [contractId, setContractId] = useState(
    run.contract_id || ""
  );

  const [month, setMonth] = useState(
    run.month || ""
  );

  const [contracts, setContracts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setContracts([]);
      setContractId("");
      return;
    }

    api.get(`/clients/${clientId}/contracts`)
      .then((r) => {
        setContracts(r.data || []);

        if (
          contractId &&
          !(r.data || []).some((c) => c.id === contractId)
        ) {
          setContractId("");
        }
      });
  }, [clientId]);

  const save = async () => {
    if (!clientId || !month) {
      toast.error("Client and payroll month are required");
      return;
    }

    try {
      setSaving(true);

      await api.put(
        `/payroll/runs/${run.id}`,
        {
          client_id: clientId,
          contract_id: contractId || null,
          month,
        }
      );

      toast.success("Payroll run updated");
      onSaved();

    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Payroll Run</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Client">
            <Select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setContractId("");
              }}
            >
              <option value="">
                Select client
              </option>

              {clients.map((c) => (
                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Contract">
            <Select
              value={contractId}
              onChange={(e) =>
                setContractId(e.target.value)
              }
            >
              <option value="">
                All / None
              </option>

              {contracts.map((c) => (
                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Payroll Month">
            <Input
              type="month"
              value={month}
              onChange={(e) =>
                setMonth(e.target.value)
              }
            />
          </Field>

          <div className="text-xs text-slate-500">
            Only Draft payroll runs can be edited.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Btn
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Btn>

          <Btn
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RunDetail({ runId, onBack, clients }) {
  const { isSuper } = useAuth();

  const [data, setData] = useState(null);
  const [importRows, setImportRows] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [calcRules, setCalcRules] = useState(null);

  const [editingRules, setEditingRules] = useState(false);
  const [editComponents, setEditComponents] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [savingRules, setSavingRules] = useState(false);

  const [q, setQ] = useState("");
  const [servicePct, setServicePct] = useState("");

  const load = () =>
    api.get(`/payroll/runs/${runId}`).then((r) => {
      setData(r.data);

      if (
        r.data?.run?.service_charge_pct !== undefined &&
        r.data?.run?.service_charge_pct !== null
      ) {
        setServicePct(
          String(r.data.run.service_charge_pct)
        );
      }
    });
  useEffect(() => { load(); }, [runId]);
  if (!data) return null;
  const { run, entries } = data;
  const cname = clients.find((c) => c.id === run.client_id)?.name || "—";
  const locked = ["finalized", "locked"].includes(run.status);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data: res } = await api.post(`/payroll/runs/${runId}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportRows(res); toast.success(`Uploaded: ${res.import.valid_rows} valid, ${res.import.error_rows} errors`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const calc = async () => {
    if (String(servicePct).trim() === "") {
      toast.error("Enter Service Charge %");
      return;
    }

    const pct = Number(servicePct);

    if (
      !Number.isFinite(pct) ||
      pct < 0
    ) {
      toast.error(
        "Enter a valid Service Charge %"
      );
      return;
    }

    try {
      const { data: res } = await api.post(
        `/payroll/runs/${runId}/calculate`,
        {
          service_charge_pct: pct,
        }
      );

      toast.success(
        `Calculated ${res.count} employees`
      );

      setImportRows(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setStatus = async (status) => {
    try {
      await api.post(`/payroll/runs/${runId}/status`, { status });
      toast.success(`Payroll ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reopenPayroll = async () => {
    const ok = window.confirm(
      `Reopen this ${run.status} payroll for editing?`
    );

    if (!ok) return;

    const reason = window.prompt(
      "Reason for reopening payroll:"
    );

    if (reason === null) return;

    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {
      await api.post(
        `/payroll/runs/${runId}/status`,
        {
          status: "calculated",
          reason: reason.trim(),
        }
      );

      toast.success("Payroll reopened for editing");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const genInvoice = async () => {
    try {
      const { data: inv } = await api.post(
        `/payroll/runs/${runId}/invoice`
      );
      toast.success(`Invoice ${inv.number} generated`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const fetchCalculationRules = async () => {
    const { data } = await api.get(
      `/payroll/runs/${runId}/calculation-rules`
    );

    setCalcRules(data);
    return data;
  };

  const openCalculationRules = async () => {
    try {
      setEditingRules(false);
      setChangeReason("");

      const rules = await fetchCalculationRules();

      setEditComponents(
        JSON.parse(
          JSON.stringify(rules.components || [])
        )
      );

      setEffectiveFrom(
        `${run.month}-01`
      );
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const startEditingRules = () => {
    setEditComponents(
      JSON.parse(
        JSON.stringify(calcRules?.components || [])
      )
    );

    setEffectiveFrom(
      `${run.month}-01`
    );

    setChangeReason("");
    setEditingRules(true);
  };

  const cancelEditingRules = () => {
    setEditingRules(false);
    setChangeReason("");

    setEditComponents(
      JSON.parse(
        JSON.stringify(calcRules?.components || [])
      )
    );
  };

  const updateComponent = (index, field, value) => {
    setEditComponents((current) =>
      current.map((c, i) =>
        i === index
          ? { ...c, [field]: value }
          : c
      )
    );
  };

  const saveCalculationRules = async () => {
    if (!effectiveFrom) {
      toast.error("Effective From date is required");
      return;
    }

    if (!changeReason.trim()) {
      toast.error("Reason for change is required");
      return;
    }

    const ok = window.confirm(
      "Save these calculation rules as a new version? Existing versions will remain unchanged."
    );

    if (!ok) return;

    try {
      setSavingRules(true);

      const components = editComponents.map((c) => {
        const copy = { ...c };

        delete copy.editable_input;

        return copy;
      });

      const { data: saved } = await api.post(
        `/payroll/runs/${runId}/calculation-rules/version`,
        {
          effective_from: effectiveFrom,
          reason: changeReason.trim(),
          components,
        }
      );

      toast.success(
        saved.profile_created
          ? `Calculation profile created · Version ${saved.version.version}`
          : `Calculation profile Version ${saved.version.version} created`
      );

      setEditingRules(false);
      setChangeReason("");

      await fetchCalculationRules();

      if (["calculated", "imported"].includes(run.status)) {
        toast.info(
          "Recalculate Payroll to apply the new formula version."
        );
      } else if (["finalized", "locked"].includes(run.status)) {
        toast.info(
          "Reopen this payroll, then recalculate to apply the new version."
        );
      }

    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingRules(false);
    }
  };

  const shown = entries.filter((e) => !q || (e.snapshot?.uan || "").includes(q) || (e.snapshot?.name || "").toLowerCase().includes(q.toLowerCase()));
  const total = (f) => entries.reduce((s, e) => s + Number(e[f] || 0), 0);

  const steps = ["draft", "imported", "calculated", "finalized", "locked"];
  const stepIdx = steps.indexOf(run.status);

  return (
    <div>
      <PageHeader title={`Payroll — ${cname}`} subtitle={`Month ${run.month} · ${run.employee_count} employees`}
        actions={<Btn variant="outline" onClick={onBack} data-testid="back-btn">← Back</Btn>} />

      {/* stepper */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {steps.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold capitalize ${i <= stepIdx ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className={`h-10 inline-flex items-center gap-2 rounded-md px-3.5 text-sm font-semibold cursor-pointer ${locked ? "bg-slate-100 text-slate-400" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="upload-excel-label">
            <Upload size={16} /> Upload Excel (UAN + Working Days)
            <input type="file" accept=".xlsx,.csv" className="hidden" disabled={locked} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="upload-excel-input" />
          </label>
          <div className="w-40">
            <label className="nk-overline block mb-1.5">
              Service Charge %
            </label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={servicePct}
              onChange={(e) =>
                setServicePct(e.target.value)
              }
              disabled={locked}
              placeholder=""
              className="h-10"
              data-testid="service-charge-pct"
            />
          </div>

          <Btn
            onClick={calc}
            disabled={
              locked ||
              run.status === "draft"
            }
            className="h-10"
            data-testid="calculate-btn"
          >
            <Calculator size={16} />
            Calculate Payroll
          </Btn>

          <Btn
            variant="outline"
            onClick={openCalculationRules}
            className="h-10"
            data-testid="calculation-rules-btn"
          >
            <Calculator size={16} /> Calculation Rules
          </Btn>
          {run.status === "calculated" && (
            <Btn
              onClick={() => setStatus("finalized")}
              data-testid="finalize-btn"
            >
              <CheckCircle2 size={16} /> Finalize
            </Btn>
          )}

          {run.status === "finalized" && (
            <Btn
              onClick={() => setStatus("locked")}
              data-testid="lock-btn"
            >
              <Lock size={16} /> Lock
            </Btn>
          )}

          {["finalized", "locked"].includes(run.status) && (
            <Btn
              variant="outline"
              onClick={reopenPayroll}
              data-testid="reopen-payroll-btn"
            >
              <RotateCcw size={16} /> Reopen for Editing
            </Btn>
          )}
          <div className="flex-1" />
          {entries.length > 0 && <>
            <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/export?format=xlsx&report=register`, `payroll_${run.month}.xlsx`)} data-testid="export-xlsx-btn"><FileSpreadsheet size={16} /> Excel</Btn>
            <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/export?format=csv&report=register`, `payroll_${run.month}.csv`)} data-testid="export-csv-btn">CSV</Btn>
            <Btn onClick={genInvoice} data-testid="gen-invoice-btn"><ReceiptText size={16} /> Generate Invoice</Btn>
          </>}
        </div>
      </Card>

      {importRows && (
        <Card className="p-4 mb-4" data-testid="import-summary">
          <div className="flex gap-6 text-sm mb-3">
            <span>Total: <b>{importRows.import.total_rows}</b></span>
            <span className="text-emerald-600">Valid: <b>{importRows.import.valid_rows}</b></span>
            <span className="text-amber-600">Warnings: <b>{importRows.import.warning_rows}</b></span>
            <span className="text-red-600">Errors: <b>{importRows.import.error_rows}</b></span>
          </div>
          <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-md">
            <table className="nk-table"><thead><tr><th>UAN</th><th>Days</th><th>Status</th><th>Messages</th></tr></thead>
              <tbody>{importRows.rows.map((r) => (
                <tr key={r.id}><td className="nk-num">{r.uan}</td><td className="nk-num">{r.working_days}</td>
                  <td><StatusBadge status={r.status === "valid" ? "finalized" : r.status === "warning" ? "calculated" : "overdue"} /></td>
                  <td className="text-slate-500 whitespace-normal">{r.messages.join(", ") || "OK"}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      {entries.length > 0 && (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="nk-head font-semibold text-sm">Payroll Register</div>
            <input placeholder="Search UAN / name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="register-search"
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
            <table className="nk-table" data-testid="payroll-register">
              <thead><tr>
                {COLS.map((c, i) => <th key={c[0]} className={i === 0 ? "nk-sticky-col" : ""}>{c[1]}</th>)}
                <th></th>
              </tr></thead>
              <tbody>
                {shown.map((e) => (
                  <tr key={e.id} className="cursor-pointer" onClick={() => setDrawer(e)} data-testid={`register-row-${e.snapshot?.uan}`}>
                    {COLS.map((c, i) => (
                      <td key={c[0]} className={`${i === 0 ? "nk-sticky-col font-semibold" : ""} ${i >= 3 ? "nk-num" : ""}`}>{get(e, c[0])}</td>
                    ))}
                    <td className="text-blue-600 text-xs">Breakdown →</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-slate-100">
                  <td className="nk-sticky-col" style={{ background: "#f1f5f9" }}>TOTAL</td><td></td><td></td><td></td><td></td>
                  <td className="nk-num">{inr(total("earned_wage"))}</td><td className="nk-num">{inr(total("employee_pf"))}</td>
                  <td className="nk-num">{inr(total("employee_esi"))}</td><td className="nk-num">{inr(total("pt"))}</td>
                  <td className="nk-num">{inr(total("total_deductions"))}</td><td className="nk-num">{inr(total("net_pay"))}</td>
                  <td className="nk-num">{inr(total("employer_pf"))}</td><td className="nk-num">{inr(total("employer_esi"))}</td>
                  <td className="nk-num">{inr(total("service_charge"))}</td><td className="nk-num">{inr(total("client_cost"))}</td><td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Calculation Rules */}
      <Sheet
        open={!!calcRules}
        onOpenChange={(o) => {
          if (!o) {
            setCalcRules(null);
            setEditingRules(false);
            setChangeReason("");
          }
        }}
      >
        <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Payroll Calculation Rules</SheetTitle>
          </SheetHeader>

          {calcRules && isSuper && (
            <div className="flex justify-end gap-2 mt-4">
              {!editingRules ? (
                <Btn
                  variant="outline"
                  onClick={startEditingRules}
                  data-testid="edit-calculation-rules"
                >
                  <Pencil size={14} />
                  Edit Rules
                </Btn>
              ) : (
                <>
                  <Btn
                    variant="outline"
                    onClick={cancelEditingRules}
                    disabled={savingRules}
                  >
                    <X size={14} />
                    Cancel
                  </Btn>

                  <Btn
                    onClick={saveCalculationRules}
                    disabled={savingRules}
                    data-testid="save-calculation-rules"
                  >
                    <Save size={14} />
                    {savingRules
                      ? "Saving..."
                      : "Save New Version"}
                  </Btn>
                </>
              )}
            </div>
          )}

          {calcRules && (
            <div className="mt-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 mb-4">
                <div className="nk-overline mb-2">
                  Calculation Source
                </div>

                <div className="text-sm font-semibold text-slate-900">
                  {calcRules.source === "contract_profile"
                    ? "Contract Calculation Profile"
                    : "System Default"}
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  {calcRules.profile?.name || "System Default"}
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  <span>
                    Payroll Month:{" "}
                    <b className="text-slate-700">
                      {calcRules.month}
                    </b>
                  </span>

                  {calcRules.profile?.version != null && (
                    <span>
                      Version:{" "}
                      <b className="text-slate-700">
                        v{calcRules.profile.version}
                      </b>
                    </span>
                  )}

                  {calcRules.profile?.effective_from && (
                    <span>
                      Effective From:{" "}
                      <b className="text-slate-700">
                        {calcRules.profile.effective_from}
                      </b>
                    </span>
                  )}
                </div>
              </div>

              {calcRules.source === "system_default" && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 mb-4 text-xs text-amber-900">
                  This contract has no explicitly assigned calculation
                  profile. Payroll is using the system-standard formula set.
                </div>
              )}

              {editingRules && (
                <div className="border border-blue-200 bg-blue-50/40 rounded-md p-4 mb-4">
                  <div className="nk-head font-semibold text-sm mb-3">
                    New Calculation Version
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <Field label="Effective From">
                      <Input
                        type="date"
                        value={effectiveFrom}
                        onChange={(e) =>
                          setEffectiveFrom(e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Reason for Change">
                      <textarea
                        value={changeReason}
                        onChange={(e) =>
                          setChangeReason(e.target.value)
                        }
                        rows={3}
                        placeholder="Example: Client-approved PF / ESI formula revision"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </Field>
                  </div>

                  <div className="text-xs text-blue-700 mt-3">
                    Saving creates a new version. Existing payroll
                    profile versions are not overwritten.
                  </div>
                </div>
              )}

              <div className="mb-2 flex items-center justify-between">
                <div className="nk-head font-semibold text-sm">
                  Formula Components
                </div>

                <div className="text-xs text-slate-400">
                  {calcRules.components?.length || 0} components
                </div>
              </div>

              <div className="space-y-2">
                {(editingRules
                  ? editComponents
                  : (calcRules.components || [])
                ).map((c, componentIndex) => {
                  const formula =
                    c.formula ||
                    (
                      c.base
                        ? `${c.base} × ${c.percent || 0}%${
                            c.ceiling
                              ? ` · ceiling ${c.ceiling}`
                              : ""
                          }`
                        : "Input / manually supplied value"
                    );

                  return (
                    <div
                      key={c.code}
                      className="border border-slate-200 rounded-md p-3 bg-white"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            {c.name}
                          </div>

                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {c.code}
                          </div>
                        </div>

                        <span
                          className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                            c.type === "INPUT"
                              ? "bg-blue-50 text-blue-700"
                              : c.type === "DEDUCTION"
                              ? "bg-rose-50 text-rose-700"
                              : c.type === "EMPLOYER"
                              ? "bg-violet-50 text-violet-700"
                              : c.type === "BILLING"
                              ? "bg-emerald-50 text-emerald-700"
                              : c.type === "TAX"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.type}
                        </span>
                      </div>

                      <div className="mt-3">
                        <div className="nk-overline mb-1">
                          Formula
                        </div>

                        {editingRules && c.type !== "INPUT" ? (
                          <textarea
                            value={c.formula || ""}
                            onChange={(e) =>
                              updateComponent(
                                componentIndex,
                                "formula",
                                e.target.value
                              )
                            }
                            rows={3}
                            className="w-full font-mono text-xs text-slate-700 bg-white border border-blue-200 rounded px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <div className="font-mono text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded px-2.5 py-2 break-words">
                            {formula}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-[11px] text-slate-400">
                        {editingRules && c.type !== "INPUT" ? (
                          <>
                            <label className="flex items-center gap-1">
                              <span>Precision</span>
                              <select
                                value={c.precision ?? 2}
                                onChange={(e) =>
                                  updateComponent(
                                    componentIndex,
                                    "precision",
                                    Number(e.target.value)
                                  )
                                }
                                className="border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-700"
                              >
                                <option value={0}>0</option>
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                                <option value={3}>3</option>
                                <option value={4}>4</option>
                              </select>
                            </label>

                            <label className="flex items-center gap-1">
                              <span>Rounding</span>
                              <select
                                value={c.rounding || "nearest"}
                                onChange={(e) =>
                                  updateComponent(
                                    componentIndex,
                                    "rounding",
                                    e.target.value
                                  )
                                }
                                className="border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-700"
                              >
                                <option value="nearest">
                                  Nearest
                                </option>
                                <option value="up">
                                  Up
                                </option>
                                <option value="down">
                                  Down
                                </option>
                              </select>
                            </label>
                          </>
                        ) : (
                          <>
                            <span>
                              Precision: {c.precision ?? 2}
                            </span>

                            <span>
                              Rounding: {c.rounding || "nearest"}
                            </span>
                          </>
                        )}

                        {c.editable_input && (
                          <span className="text-blue-600">
                            Editable monthly input
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-[420px] overflow-y-auto">
          <SheetHeader><SheetTitle>Calculation Breakdown</SheetTitle></SheetHeader>
          {drawer && (
            <div className="mt-4">
              <div className="text-sm font-semibold">{drawer.snapshot?.name} · {drawer.snapshot?.uan}</div>
              <div className="text-xs text-slate-500 mb-4">{drawer.snapshot?.designation}</div>
              <div className="space-y-1" data-testid="breakdown-list">
                {(drawer.components || []).map((c, i) => (
                  <div key={i} className="flex items-start justify-between border-b border-slate-100 py-2">
                    <div><div className="text-sm text-slate-700">{c.label}</div><div className="text-[11px] text-slate-400">{c.note}</div></div>
                    <div className="nk-num text-sm font-semibold">{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
